import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import type { DeadlineAssigneeCandidateDto } from '@prospero/shared';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/use-is-mobile';

export interface AssigneeAutocompleteProps {
  value: string | null;
  candidates: readonly DeadlineAssigneeCandidateDto[];
  isLoading: boolean;
  isError: boolean;
  onChange: (userId: string | null) => void;
  disabled?: boolean;
  helperText?: string;
  label?: string;
  testId?: string;
}

export function AssigneeAutocomplete({
  value,
  candidates,
  isLoading,
  isError,
  onChange,
  disabled = false,
  helperText,
  label,
  testId = 'assignee-autocomplete',
}: AssigneeAutocompleteProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const selectedCandidate = candidates.find((candidate) => candidate.userId === value) ?? null;
  const resolvedHelper = isError ? t('documents.deadlines.assignee.loadError') : helperText;

  return (
    <Autocomplete
      options={[...candidates]}
      value={selectedCandidate}
      loading={isLoading}
      disabled={disabled}
      getOptionLabel={(candidate) => candidate.name}
      isOptionEqualToValue={(option, selected) => option.userId === selected.userId}
      onChange={(_event, candidate) => onChange(candidate?.userId ?? null)}
      noOptionsText={t('documents.deadlines.assignee.noOptions')}
      fullWidth
      data-testid={testId}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('documents.deadlines.assignee.label')}
          placeholder={t('documents.deadlines.assignee.placeholder')}
          error={isError}
          helperText={resolvedHelper}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps?.htmlInput,
              autoComplete: 'off',
              style: { fontSize: isMobile ? '16px' : undefined },
            },
            input: {
              ...params.slotProps?.input,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress size={20} /> : null}
                  {params.slotProps?.input?.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
